import React, { useState, useRef } from 'react';
import { Upload, X, Image, File, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FileUploadProps {
  onUploadComplete: (url: string) => void;
  onUploadError: (error: string) => void;
  acceptedTypes?: string[];
  maxSizeInMB?: number;
  bucket?: string;
  folder?: string;
  currentImageUrl?: string;
  className?: string;
  disabled?: boolean;
}

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  fileName: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onUploadComplete,
  onUploadError,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  maxSizeInMB = 10,
  bucket = 'portfolio-images',
  folder = 'uploads',
  currentImageUrl,
  className = '',
  disabled = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
    fileName: ''
  });
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!acceptedTypes.includes(file.type)) {
      return `Tipe file tidak didukung. Gunakan: ${acceptedTypes.map(type => type.split('/')[1]).join(', ')}`;
    }

    // Check file size
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return `Ukuran file terlalu besar. Maksimal ${maxSizeInMB}MB`;
    }

    return null;
  };

  const generateFileName = (file: File): string => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const fileExtension = file.name.split('.').pop();
    return `${timestamp}-${randomString}.${fileExtension}`;
  };

  const uploadFile = async (file: File) => {
    setError(null);
    setUploadProgress({
      isUploading: true,
      progress: 0,
      fileName: file.name
    });

    try {
      const fileName = generateFileName(file);
      const filePath = `${folder}/${fileName}`;

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90)
        }));
      }, 200);

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      clearInterval(progressInterval);

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      // Get public URL
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      setUploadProgress(prev => ({ ...prev, progress: 100 }));
      
      setTimeout(() => {
        setUploadProgress({
          isUploading: false,
          progress: 0,
          fileName: ''
        });
        onUploadComplete(data.publicUrl);
      }, 500);

    } catch (err) {
      setUploadProgress({
        isUploading: false,
        progress: 0,
        fileName: ''
      });
      const errorMessage = err instanceof Error ? err.message : 'Gagal mengupload file';
      setError(errorMessage);
      onUploadError(errorMessage);
    }
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      onUploadError(validationError);
      return;
    }

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    uploadFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploadProgress.isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
    // Reset input value to allow selecting the same file again
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && !uploadProgress.isUploading) {
      fileInputRef.current?.click();
    }
  };

  const clearPreview = () => {
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isImage = (url: string) => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={acceptedTypes.join(',')}
        onChange={handleInputChange}
        disabled={disabled || uploadProgress.isUploading}
      />

      {/* Upload area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
          ${disabled || uploadProgress.isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
          ${error ? 'border-red-300 bg-red-50' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {/* Preview */}
        {previewUrl && (
          <div className="mb-4">
            <div className="relative inline-block">
              {isImage(previewUrl) ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-h-32 max-w-full object-contain rounded-md"
                />
              ) : (
                <div className="flex items-center justify-center w-32 h-32 bg-gray-100 rounded-md">
                  <File className="h-8 w-8 text-gray-400" />
                </div>
              )}
              {!uploadProgress.isUploading && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearPreview();
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  title="Hapus file"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            {/* Upload ulang button */}
            {!uploadProgress.isUploading && (
              <div className="mt-2 flex justify-center space-x-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors flex items-center"
                  title="Upload ulang file"
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Upload Ulang
                </button>
              </div>
            )}
          </div>
        )}

        {/* Upload progress */}
        {uploadProgress.isUploading && (
          <div className="mb-4">
            <div className="flex items-center justify-center mb-2">
              <Upload className="h-6 w-6 text-blue-500 animate-pulse mr-2" />
              <span className="text-sm text-gray-600">Mengupload {uploadProgress.fileName}...</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.progress}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {uploadProgress.progress}%
            </div>
          </div>
        )}

        {/* Upload icon and text */}
        {!previewUrl && !uploadProgress.isUploading && (
          <div>
            <Image className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="text-sm text-gray-600">
              <span className="font-medium text-blue-600 hover:text-blue-500">
                Klik untuk upload
              </span>
              {' atau drag and drop'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')} hingga {maxSizeInMB}MB
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-4 flex items-center justify-center text-red-600">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};